import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import { Tooltip } from '@mui/material';
import { v4 as uuid } from 'uuid';
import _ from 'lodash';

import { storage } from 'src/app/database';

import { Loader } from '../Loader/Loader';
import { Modal } from '../Modal/Modal';
import { Button } from '../Button/Button';

import DownloadIcon from 'src/assets/dropZone/download.svg';
import CloseIcon from 'src/assets/dropZone/circleClose.svg';
import FileUploadIcon from 'src/assets/dropZone/fileUpload.svg';
import CheckIcon from 'src/assets/dropZone/checkIcon.svg';

import classes from './dropZone.module.scss';

export const DropZone = (props) => {
  const { label, path, maxFiles = 100, isDisabled } = props;

  const [files, setFiles] = useState([]);
  const [fileFullPath, setFileFullPath] = useState('');
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isDeleteModal, setIsDeleteModal] = useState(false);

  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const safeSet = useMemo(() => {
    return {
      loading: (v) => isMountedRef.current && setIsLoadingFiles(v),
      files: (updater) => isMountedRef.current && setFiles(updater),
      fullPath: (v) => isMountedRef.current && setFileFullPath(v),
      deleteModal: (v) => isMountedRef.current && setIsDeleteModal(v),
    };
  }, []);

  const loadFilesFromStorage = useCallback(async () => {
    const storageRef = storage.ref();
    const listRef = storageRef.child(`${path}`);

    safeSet.loading(true);

    try {
      const res = await listRef.listAll();
      if (!isMountedRef.current) return;

      const converted = [];

      for (const itemRef of res.items) {
        const downloadUrl = await itemRef.getDownloadURL();
        if (!isMountedRef.current) return;

        const meta = await itemRef.getMetadata();
        if (!isMountedRef.current) return;

        converted.push({
          downloadUrl,
          fullPath: meta.fullPath,
          fileName: meta.name,
          contentType: meta.contentType,
          id: uuid(),
        });

        if (maxFiles === 1) break;
      }

      safeSet.files(maxFiles === 1 ? (converted[0] ? [converted[0]] : []) : converted);
    } catch (e) {
      console.error(e);
    } finally {
      safeSet.loading(false);
    }
  }, [path, maxFiles, safeSet]);

  useEffect(() => {
    loadFilesFromStorage();
  }, [loadFilesFromStorage]);

  const onDrop = useCallback(
    async (droppedFiles) => {
      safeSet.loading(true);

      try {
        for (const file of droppedFiles) {
          const reader = new FileReader();

          reader.onabort = () => console.warn('file reading was aborted');
          reader.onerror = () => console.warn('file reading has failed');

          reader.onload = async () => {
            if (!isMountedRef.current) return;

            try {
              const binaryStr = reader.result;

              const storageRef = storage.ref();
              const fileRef = storageRef.child(`${path}${file.path}`);

              const metaData = { contentType: file.type };

              const response = await fileRef.put(binaryStr, metaData);
              if (!isMountedRef.current) return;

              const meta = response.metadata;

              const payload = {
                downloadUrl: await response.ref.getDownloadURL(),
                fullPath: meta.fullPath,
                fileName: meta.name,
                contentType: meta.contentType,
                isNewFile: true,
                id: uuid(),
              };

              if (!isMountedRef.current) return;

              safeSet.files((prev) => {
                const next = [...prev, payload];
                return maxFiles === 1 ? next.slice(-1) : next;
              });
            } catch (e) {
              console.error(e);
            } finally {
              safeSet.loading(false);
            }
          };

          reader.readAsArrayBuffer(file);
        }
      } catch (e) {
        console.error(e);
        safeSet.loading(false);
      }
    },
    [path, maxFiles, safeSet],
  );

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    maxFiles,
    disabled: isDisabled,
    multiple: maxFiles !== 1,
    accept:
      'text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/pdf',
  });

  useEffect(() => {
    if (!fileRejections.length) return;

    const errorNames = {};

    for (const item of fileRejections) {
      for (const error of item.errors) {
        errorNames[error.code] = error.message;
      }
    }

    for (const code in errorNames) {
      if (code === 'file-invalid-type') {
        toast('File should be one of type - CSV, XLS, XLSX or PDF', { type: 'error' });
      } else {
        toast(errorNames[code], { type: 'error' });
      }
    }

    safeSet.loading(false);
  }, [fileRejections, safeSet]);

  const onOpenDeleteModal = useCallback(
    (fullPath) => () => {
      safeSet.fullPath(fullPath);
      safeSet.deleteModal(true);
    },
    [safeSet],
  );

  const onCloseDeleteModal = useCallback(() => {
    safeSet.fullPath('');
    safeSet.deleteModal(false);
  }, [safeSet]);

  const onRemoveFile = useCallback(async () => {
    try {
      const storageRef = storage.ref();
      await storageRef.child(fileFullPath).delete();
      if (!isMountedRef.current) return;

      safeSet.files((prev) => _.reject(prev, (el) => el.fullPath === fileFullPath));
    } catch (e) {
      console.error(e);
    } finally {
      safeSet.deleteModal(false);
      safeSet.fullPath('');
    }
  }, [fileFullPath, safeSet]);

  const onDownloadFile = useCallback(
    (fullPath) => () => {
      const file = files.find((item) => item.fullPath === fullPath);
      if (!file) return;
      saveAs(file.downloadUrl, file.fileName);
    },
    [files],
  );

  return (
    <>
      {isDeleteModal && (
        <Modal type="small" closeButtonType="inside" onClose={onCloseDeleteModal}>
          <div className={classes.ModalContent}>
            <div className={classes.ModalContentTitle}>
              Are you sure you want to delete this file?
            </div>
            <div className={classes.ModalContentFooter}>
              <Button type="secondary" title="Cancel" onClick={onCloseDeleteModal} />
              <Button type="danger" title="Yes" onClick={onRemoveFile} />
            </div>
          </div>
        </Modal>
      )}

      <div className={classes.DropZoneWrapper}>
        {label && <div className={classes.DropZoneLabel}>{label}</div>}

        <section className={!isDisabled ? classes.DropZone : classes.DropZoneDisabled}>
          {isLoadingFiles ? (
            <div className={classes.DropZoneArea}>
              <Loader size="small" />
            </div>
          ) : (
            <>
              {files.length > 0 && (
                <aside
                  className={
                    maxFiles === 1 ? classes.DropZoneContentSingle : classes.DropZoneContent
                  }
                >
                  {files.map((file) => (
                    <div className={classes.DropZoneContentItem} key={file.fullPath}>
                      <div
                        className={file.isNewFile ? classes.DropZoneContentItemWrapper : undefined}
                        onClick={onDownloadFile(file.fullPath)}
                      >
                        <img
                          className={classes.DropZoneContentItemDownload}
                          id="downloadIcon"
                          src={DownloadIcon}
                          alt="DownloadIcon"
                        />
                        <img
                          className={classes.DropZoneContentItemCheck}
                          id="checkIcon"
                          src={CheckIcon}
                          alt="CheckIcon"
                        />
                      </div>

                      {file.fileName.length > 12 ? (
                        <Tooltip title={file.fileName}>
                          <span>
                            {file.fileName.slice(0, 13)}
                            ...
                          </span>
                        </Tooltip>
                      ) : (
                        <span>{file.fileName}</span>
                      )}

                      {!isDisabled && (
                        <img
                          className={classes.DropZoneContentItemClose}
                          onClick={onOpenDeleteModal(file.fullPath)}
                          src={CloseIcon}
                          alt="CloseIcon"
                        />
                      )}
                    </div>
                  ))}
                </aside>
              )}

              {(files.length === 0 || maxFiles !== 1) && (
                <div
                  {...getRootProps({
                    className: `dropzone ${
                      files.length > 0 ? classes.DropZoneAreaGrey : classes.DropZoneArea
                    }`,
                  })}
                  style={{ cursor: !isDisabled ? 'pointer' : 'default' }}
                >
                  {!isDisabled ? (
                    <>
                      <input {...getInputProps()} />
                      <div className={classes.DropZoneUpload}>
                        <img
                          className={classes.DropZoneUploadImage}
                          src={FileUploadIcon}
                          alt="FileUpload"
                        />
                        {maxFiles === 1 ? 'Drop File Here' : 'Drop Files Here'}
                      </div>
                    </>
                  ) : (
                    <div className={classes.DropZoneNotUploaded}>
                      {maxFiles === 1 ? 'No files uploaded' : 'Here is no files uploaded'}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
};
